/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, select, call, race, fork, CallEffect, takeLatest, take, spawn } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  Asset,
  AssetMediaType,
  AssetOption,
  AssetUtils,
  AsyncActionStateNoData,
  Channel,
  ContentTagsUtils,
  Ducks,
  EntryPoint,
  FileFormat,
  Folder,
  FoldersUtils,
  PlayableAsset,
  ProductPrice,
  ProductPriceApi,
  Program,
  RecordingStatus,
  RecordingsUtils,
  SubscriptionType,
  XError,
} from '@wit/vtv-x-foundation-lib';
import Seasons, { SeasonsData } from 'core/store/ducks/pi/seasons';
import BestEpisode from 'core/store/ducks/pi/seriesBestEpisode';
import Clones from 'core/store/ducks/pi/clones';
import Subscriptions from 'core/store/ducks/pi/subscriptions';
import Collections from 'core/store/ducks/pi/collections';
import Related, { RelatedRail, RelatedRailType } from 'core/store/ducks/pi/related';
import { Selectors as ChannelsSelectors } from 'core/store/ducks/epg/channels';
import { callWithProgress } from 'store/ducks/layoutProgressBar';
import translate from 'services/i18n/translate';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';
import * as Player from 'store/ducks/player';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import * as Data from '../data';
import * as Context from '../context';
import { getOptions, getLiveWatchOptions } from './getOptions';
import AssetInfoUtils from '../../../utils/assetInfoUtils';

// next, then and later programs
const NUMBER_OF_NEXT_PROGRAMS = 3;

const BADGES_PRIORITY = BADGES_PRIORITY_PER_SECTION_RAIL.ALL.RELATED_RAILS;

export enum ActionType {
  REQUEST = 'programInfo/loadInfo',
  SUCCESS = 'programInfo/loadInfo/SUCCESS',
  FAIL = 'programInfo/loadInfo/FAIL',
}

// ACTIONS
type LoadInfo = ActionRequest<ActionType.REQUEST, Context.PiContext>;
type LoadInfoFail = ActionFail<ActionType.FAIL, Context.PiContext>;
type LoadInfoSuccess = ActionSuccess<ActionType.SUCCESS, undefined, Context.PiContext>;

type Actions = LoadInfo | LoadInfoFail | LoadInfoSuccess | Context.Actions;

const Actions = {
  request: (context: Context.PiContext): LoadInfo => ({
    type: ActionType.REQUEST,
    meta: context,
  }),
  success: (context: Context.PiContext): LoadInfoSuccess => ({
    type: ActionType.SUCCESS,
    meta: context,
  }),
  fail: (context: Context.PiContext, error: XError): LoadInfoFail => ({
    type: ActionType.FAIL,
    error,
    meta: context,
  }),
};

export const Creators = { request: Actions.request };

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState, ctx: Context.PiContext): boolean => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.actions.loadInfo[contextId]?.processing === true;
  },
  isProcessed: (state: StoreState, ctx: Context.PiContext): boolean => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.actions.loadInfo[contextId]?.processed === true;
  },
  getError: (state: StoreState, ctx: Context.PiContext): XError | undefined => {
    const contextId = Context.getContextId(ctx);
    return state.programInfo.actions.loadInfo[contextId]?.error;
  },
};

function getSeasonLabel(seasonNumber: number): string {
  if (AssetUtils.isSpecialSeasonNumber(seasonNumber)) {
    return translate['programInfo.moreepisodes.seasonSpecial'];
  }

  if (!AssetUtils.isValidSeasonNumber(seasonNumber)) {
    return translate['programInfo.moreepisodes.seasonWithoutNumber'];
  }

  return seasonNumber.toString();
}

function getRelatedRailLabel(rail: RelatedRail, asset: Asset, relatedChannel?: Channel): string {
  const railName = rail.name || '';
  switch (rail.type) {
    case RelatedRailType.ACTOR:
      return translate
        .formatString(translate['programInfo.related.actors'], AssetUtils.normalizeName(railName))
        .toString();
    case RelatedRailType.DIRECTOR:
      return translate
        .formatString(translate['programInfo.relatedbydirector'], AssetUtils.normalizeName(railName))
        .toString();
    case RelatedRailType.GENRE: {
      if (rail.isRecomm) {
        return translate.formatString(translate['programInfo.relatedbygenre'], railName).toString();
      }
      switch (asset.mediaType) {
        case AssetMediaType.EPISODE:
        case AssetMediaType.MOVIE:
        case AssetMediaType.SERIES:
          return translate.formatString(translate['programInfo.related.vod'], asset.title).toString();
        case AssetMediaType.EPG:
        case AssetMediaType.RECORDING:
          return translate.formatString(translate['programInfo.related.epg'], relatedChannel?.title || '').toString();
        default:
          return '';
      }
    }
    case RelatedRailType.RELATED_CONTENT:
      return translate.formatString(translate['programInfo.relatedto'], railName).toString();
    default:
      return '';
  }
}

function adaptSeasonDataToUi(seasonsData: SeasonsData): Data.PiRailUi[] {
  const seasons: Data.PiRailUi[] = [];

  const entries = Object.entries(seasonsData.episodesBySeason);
  let seasonLabel: string;

  for (let i = 0; i < entries.length; i += 1) {
    const [seasonNumber, episodes] = entries[i];

    seasonLabel = getSeasonLabel(Number(seasonNumber));

    const episodesWithBadges: Asset[] = RailBadgeEngine.getAssetsWithBadgesListFromRail(
      episodes,
      BADGES_PRIORITY_PER_SECTION_RAIL.ALL.MORE_EPISODES_RAILS
    );

    seasons.push({
      key: seasonNumber,
      title: seasonLabel,
      assets: episodesWithBadges,
    });
  }

  return seasons;
}

// SAGAS

function* getSeasons(entryAsset: Asset, entryPoint?: EntryPoint): SagaIterator<SeasonsData | undefined> {
  try {
    const seasonsData: SeasonsData = yield call(Seasons.getSeriesSeasons, entryAsset, entryPoint, true);
    return seasonsData;
  } catch {
    return undefined;
  }
}

function* getBestEpisode(
  ctx: Context.PiContext,
  seasonsData: SeasonsData,
  asset: Asset
): SagaIterator<Asset | undefined> {
  try {
    if (
      ctx.entryPoint === EntryPoint.CLU ||
      ctx.entryPoint === EntryPoint.TV_GUIDE ||
      ctx.entryPoint === EntryPoint.SEASONS ||
      ctx.entryPoint === EntryPoint.SCHEDULED_RECORDINGS ||
      (ctx.entryPoint === EntryPoint.RECORDINGS &&
        AssetUtils.isRecording(asset) &&
        RecordingsUtils.getRecordingStatus(asset) === RecordingStatus.SCHEDULED)
    ) {
      return undefined;
    }
    const bestEpisode: Asset | undefined = yield call(
      BestEpisode.getBestEpisode,
      seasonsData,
      asset,
      ctx.entryMenuItem
    );
    return bestEpisode;
  } catch {
    return undefined;
  }
}

function* getClones(infoAsset: Asset, entryPoint?: EntryPoint, seasonsData?: SeasonsData): SagaIterator<Asset[]> {
  let clones: Asset[] | undefined;
  try {
    if (entryPoint === EntryPoint.CLU || entryPoint === EntryPoint.TV_GUIDE) {
      return [infoAsset];
    }

    if (AssetUtils.hasValidSeriesId(infoAsset)) {
      clones = seasonsData?.episodesClones[(infoAsset as PlayableAsset).internalId];
    } else {
      clones = yield call(Clones.getAssetClones, infoAsset, entryPoint);
    }

    return clones || [infoAsset];
  } catch {
    return [infoAsset];
  }
}

function* getFolders(clones: Asset[]): SagaIterator<Folder[]> {
  try {
    // clones include: AssetMediaType.EPISODE, AssetMediaType.MOVIE, AssetMediaType.RECORDING and AssetMediaType.EPG
    const subscriptionsFolders = yield call(Subscriptions.getRelatedSubscriptionsFolders, clones);
    const collectionsFolders = yield call(Collections.getRelatedCollectionFolders, clones);
    return [...subscriptionsFolders, ...collectionsFolders];
  } catch (error) {
    return [];
  }
}

function* getVodsPrices(clones: Asset[], entryPoint?: EntryPoint): SagaIterator<ProductPrice[]> {
  if (entryPoint === EntryPoint.CLU || entryPoint === EntryPoint.TV_GUIDE) {
    return [];
  }
  const fileIds: number[] = [];
  clones.forEach((clone) => {
    if (AssetUtils.isVod(clone) && ContentTagsUtils.isPlaybackAllowedForVodAsset(clone)) {
      clone.streams.forEach((stream) => {
        if (stream.fileFormat !== FileFormat.TRAILER) {
          fileIds.push(stream.id);
        }
      });
    }
  });

  if (fileIds.length) {
    const currency: string = yield select(Ducks.Currency.Selectors.getCurrencyCode);
    const vodsPrices: ProductPrice[] = yield Ducks.Api.callApi(ProductPriceApi.listByFileIds, fileIds);
    return vodsPrices.filter((vodPrice) => vodPrice.price.currency === currency);
  }

  return [];
}

function* getRailUiFromRelatedRail(
  relatedRail: RelatedRail,
  asset: Asset,
  relatedChannel?: Channel
): SagaIterator<Data.PiRailUi | undefined> {
  if (relatedRail.assets.length === 0) {
    return undefined;
  }

  const assetsUpdated: Asset[] = yield call(
    RailBadgeEngine.getAssetsWithBadgesListFromRail,
    relatedRail.assets,
    BADGES_PRIORITY
  );
  // assetsUpdated = AssetUiUtils.getAssetUiWithAgeRatingUpdated(assetsUpdated) as Asset[];

  return {
    title: getRelatedRailLabel(relatedRail, asset, relatedChannel),
    assets: assetsUpdated,
    key: `${relatedRail.id}`,
    menuItem: relatedRail.menuItem,
  };
}

function* loadRelated(ctx: Context.PiContext, asset: Asset, relatedChannel?: Channel): SagaIterator {
  try {
    const related: RelatedRail[] = yield call(Related.getRelated, asset);

    // UI adaptation
    const relatedRailsUi: Data.PiRailUi[] = [];

    for (let i = 0; i < related.length; i += 1) {
      const relatedRailUi: Data.PiRailUi | undefined = yield call(
        getRailUiFromRelatedRail,
        related[i],
        asset,
        relatedChannel
      );

      if (relatedRailUi) {
        relatedRailsUi.push(relatedRailUi);
      }
    }

    yield put(Data.ActionCreators.update({ context: ctx, data: { related: relatedRailsUi } }));
  } catch {
    //
  }
}

function* getFolderRailWithAssets(folder: Folder, channelId: number): SagaIterator<Data.PiRailUi | undefined> {
  const assets: Asset[] = yield call(Subscriptions.getAssetsByChannel, channelId);
  if (assets.length) {
    // let assetsUpdated = yield select(BookmarksSelectors.getAssetsWithProgress, assets);
    // assetsUpdated = RailBadgeEngine.getAssetsWithBadgesFromRail(assetsUpdated);
    // assetsUpdated = AssetUiUtils.getAssetUiWithAgeRatingUpdated(assetsUpdated) as Asset[];
    const assetsWithBadges: Asset[] = yield call(
      RailBadgeEngine.getAssetsWithBadgesListFromRail,
      assets,
      BADGES_PRIORITY
    );

    return {
      key: channelId.toString(),
      title: folder.title,
      assets: [folder, ...assetsWithBadges],
    };
  }

  return undefined;
}

function* loadFoldersRelated(ctx: Context.PiContext, folders: Folder[]): SagaIterator {
  // NOTE: for now only subscriptions folders are considered, probably collections will be also considered

  try {
    // linear folders are not presented in the UI
    const foldersForUi = folders.filter((folder) => folder.productType !== SubscriptionType.LINEAR);

    // for the valid folders (ones with at least one valid channel)
    // -creates a rail with the folder name
    // -load the assets for the folder's channel
    const assetsLoadingTasks: CallEffect[] = [];
    foldersForUi.forEach((folder) => {
      const channelId = FoldersUtils.getMainChannelId(folder);
      assetsLoadingTasks.push(call(getFolderRailWithAssets, folder, channelId));
    });

    // wait for all rails be completed
    const railsResults: (Data.PiRailUi | undefined)[] = yield all(assetsLoadingTasks);

    // update reducer with the valid rails (rails with assets)
    yield put(
      Data.ActionCreators.update({
        context: ctx,
        data: { foldersRelated: railsResults.filter((rail) => rail !== undefined) as Data.PiRailUi[] },
      })
    );
  } catch {
    //
  }
}

function* getRelatedChannel(infoAsset: Asset): SagaIterator<Channel | undefined> {
  const channels: Channel[] = yield select(ChannelsSelectors.getChannels);
  return AssetUtils.getAssetRelatedChannel(infoAsset, channels);
}

function* getCurrentProgram(channelEpgId: number): SagaIterator<Program | undefined> {
  return yield call(Ducks.Epg.Live.getLiveProgram, channelEpgId);
}

function* loadNextPrograms(ctx: Context.PiContext, currentProgram: Program): SagaIterator {
  const nextPrograms: Program[] | undefined = yield call(
    Ducks.Epg.Programs.getFuturePrograms,
    currentProgram.channelEpgId,
    currentProgram,
    NUMBER_OF_NEXT_PROGRAMS
  );

  if (nextPrograms && nextPrograms.length > 0) {
    yield put(Data.ActionCreators.update({ context: ctx, data: { nextPrograms } }));
  }
}

function* loadVodsPrices(ctx: Context.PiContext, clones: Asset[]): SagaIterator {
  const vodPrices: ProductPrice[] = yield call(getVodsPrices, clones, ctx.entryPoint);
  yield put(Data.ActionCreators.update({ context: ctx, data: { vodPrices } }));
}

function* loadAssetOptions(
  ctx: Context.PiContext,
  asset: Asset,
  clones: Asset[],
  folders: Folder[],
  seasonsData?: SeasonsData
): SagaIterator {
  yield all([call(loadVodsPrices, ctx, clones), call(Ducks.BookmarksCache.loadBookmarks, clones)]);

  const options: AssetOption[] = yield call(getOptions, ctx, asset, clones, folders, seasonsData);
  if (options.length > 0) {
    yield put(Data.ActionCreators.update({ context: ctx, data: { options } }));
  }
}

function* loadLiveWatchOptions(ctx: Context.PiContext, currentProgram: Program): SagaIterator {
  const options: AssetOption[] = yield call(getLiveWatchOptions, currentProgram);
  if (options.length > 0) {
    yield put(Data.ActionCreators.update({ context: ctx, data: { options } }));
  }
}

function* getEntryAsset(ctx: Context.PiContext): SagaIterator<Asset | undefined> {
  // the source of the asset may depend on: entry point or media type

  // // when we entry the PI from recordings or purchase rails
  // switch (ctx.entryPoint) {
  //   case EntryPoint.RECORDINGS: {
  //     const recordings: Recording[] = yield select(RecordingsSelectors.getRecordings);
  //     return recordings.find((recording) => recording.id === ctx.entryAssetId);
  //     // TODO: adicionar um fallback para ir à rede uma vez q os recordings podem ainda n ter sido todos carregados
  //   }
  //   case EntryPoint.PURCHASES: {
  //     // TODO: os assets das compras podem n estar carregados em situação de reload do link do pi
  //     // é preciso pensar se faz sentido tentar carregar de uma store incompleta ou então fazer redirect para o mytv
  //     const purchasedAssets: Asset[] = yield select(PurchasesSelectors.getAssets);
  //     return purchasedAssets.find((asset) => asset.id === ctx.entryAssetId);
  //   }
  //   default:
  //     break;
  // }

  if (
    ctx.entryPoint === EntryPoint.RECORDINGS ||
    ctx.entryPoint === EntryPoint.SCHEDULED_RECORDINGS ||
    ctx.entryPoint === EntryPoint.PURCHASES ||
    ctx.entryAssetMediaType === AssetMediaType.LINEAR
  ) {
    return undefined;
  }

  // for the remaining scenarios we will check the media type
  switch (ctx.entryAssetMediaType) {
    case AssetMediaType.EPG:
      return yield call(AssetInfoUtils.getProgram, ctx.entryAssetId);
    default:
      return yield call(AssetInfoUtils.getMedia, ctx.entryAssetId);
  }
}

function* handleLiveProgramRollover(ctx: Context.PiContext, curPiProgram: Program): SagaIterator {
  // listen live asset updates and pi clear actions
  const [liveAssetUpdateAction]: [Player.SetCurrentAsset | undefined] = yield race([
    take(Player.ActionType.SET_CURRENT_ASSET),
    Context.takeCtx(Context.ActionType.CLEAR, ctx),
  ]);

  if (liveAssetUpdateAction && liveAssetUpdateAction.meta.currentAsset?.id !== curPiProgram.id) {
    // player updates the live asset, then updates the PI
    yield put(Actions.request(ctx));
  }
}

function* loadInfoFlow(action: LoadInfo): SagaIterator {
  let asset: Asset;
  let relatedChannel: Channel | undefined;
  let seasonsData: SeasonsData | undefined;

  const entryAsset: Asset | undefined = action.meta.entryAsset || (yield call(getEntryAsset, action.meta));

  if (!entryAsset) {
    yield put(Actions.fail(action.meta, XError.fromError('content not found')));
    return;
  }

  yield put(Context.ActionCreators.start(action.meta));

  if (AssetUtils.isChannel(entryAsset)) {
    // LIVE PI - entry asset is a CHANNEL
    relatedChannel = entryAsset;
    const curProgram: Program | undefined = yield call(getCurrentProgram, entryAsset.epgId);
    if (curProgram) {
      asset = curProgram;
      yield call(loadLiveWatchOptions, action.meta, curProgram);
      yield fork(loadNextPrograms, action.meta, curProgram);
      yield spawn(handleLiveProgramRollover, action.meta, curProgram);
    } else {
      // if for some reason current program is not available stop here the flow
      put(Actions.fail(action.meta, XError.fromError('content not found')));
      return;
    }
  } else {
    // non LIVE PI - entry asset may be a: MOVIE, EPISODE, SERIES, PROGRAM/EPG
    asset = entryAsset;
    relatedChannel = yield call(getRelatedChannel, asset);
  }

  // check the series logic - build the seasons
  // valid for assets media type: SERIES, EPISODE or PRAGRAM that have a valid seriesID
  if (AssetUtils.hasValidSeriesId(asset)) {
    seasonsData = yield call(getSeasons, asset, action.meta.entryPoint);
    if (seasonsData) {
      yield put(
        Data.ActionCreators.update({
          context: action.meta,
          data: { seasonsData, seasons: adaptSeasonDataToUi(seasonsData) },
        })
      );

      const bestEpisode = yield call(getBestEpisode, action.meta, seasonsData, asset);
      if (bestEpisode) {
        // the best episode will be the info Asset
        asset = bestEpisode;
      }
    }
  }

  yield put(Data.ActionCreators.update({ context: action.meta, data: { asset, relatedChannel } }));

  const clones: Asset[] = yield call(getClones, asset, action.meta.entryPoint, seasonsData);
  yield put(Data.ActionCreators.update({ context: action.meta, data: { clones } }));

  const folders: Folder[] = yield call(getFolders, clones);
  yield put(Data.ActionCreators.update({ context: action.meta, data: { folders } }));

  // options
  yield fork(loadAssetOptions, action.meta, asset, clones, folders, seasonsData);

  // related
  yield fork(loadRelated, action.meta, asset, relatedChannel);
  yield fork(loadFoldersRelated, action.meta, folders);

  yield put(Actions.success(action.meta));
}

function* requestSaga(action: LoadInfo): SagaIterator {
  // a clear action will cancel a running load info for the same context
  yield race([callWithProgress(loadInfoFlow, action), Context.takeCtx(Context.ActionType.CLEAR, action.meta)]);
}

export function* saga(): SagaIterator {
  yield all([takeLatest(ActionType.REQUEST, requestSaga)]);
}

// REDUCER
type ReducerState = Record<string, AsyncActionStateNoData>;
const initialState: ReducerState = {};

export default function loadInfo(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.REQUEST: {
      const contextId = Context.getContextId(action.meta);
      return {
        ...state,
        [contextId]: { processing: true, processed: false },
      };
    }
    case ActionType.SUCCESS: {
      const contextId = Context.getContextId(action.meta);
      return {
        ...state,
        [contextId]: { processing: false, processed: true },
      };
    }
    case ActionType.FAIL: {
      const contextId = Context.getContextId(action.meta);
      return {
        ...state,
        [contextId]: { processing: false, processed: true, error: action.error },
      };
    }
    case Context.ActionType.CLEAR: {
      const contextId = Context.getContextId(action.meta);

      const newState = { ...state };
      delete newState[contextId];

      return newState;
    }
    default:
      return state;
  }
}
